import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDispatch } from 'hooks';
import dealersActions from 'modules/dealers/actions';
import { getAppOrigin, getAppTheme } from 'modules/app/selectors';
import { getAvailableThemes } from 'modules/refData/selectors';
import { getConfidentialityPolicyLink, getLegalNotice } from 'modules/selfServices/selectors';
import Button, { ButtonSize, ButtonType } from 'components/ui/Button';
import {
  getPaymentMethods,
  isPublicDevice,
  shouldDisplayPhoneNumbers as shouldDisplayPhoneNumbersSelector,
} from 'modules/dealers/selectors';

import { b } from 'utils/i18nUtils';
import { ucFirst } from 'utils/global';
import { getHyperlink } from 'utils/linkUtils';
import { createDictionary } from 'utils/objectUtils';
import addHtmlAttribute, { isDevelopment } from 'utils/appUtils';

import Modal from '../Modal';
import SelectInput from '../SelectInput';
import DealersPhone from '../DealersPhone';
import QRCodeViewer from '../QRCodeViewer';
import HelpTerms from './HelpTerms';

const DSA_URL = 'https://www.fleetback.com/digital-services-act/';

/**
 * Dev tooling: preview the UI as another device. Keys are the `data-origin` values used by the
 * Tailwind variants. `REMOTE_KIOSK` (Sharebox outdoor) is deliberately absent: it renders on the
 * customer's own phone, so it is already covered by the mobile display.
 */
const KIOSK_DISPLAYS: Record<string, string> = {
  HOME: 'Mobile (home)',
  KIOSK: 'Partteam kiosk',
  SHAREBOX_INDOOR_KIOSK: 'Sharebox indoor kiosk',
};
/** Displays rendered on a kiosk screen, where the device is public (kiosk-only controls such as the footer button). */
const PUBLIC_KIOSK_DISPLAYS = ['KIOSK', 'SHAREBOX_INDOOR_KIOSK'];

enum LinkTypes {
  DSA = 'DSA',
  LEGAL_NOTICE = 'LEGAL_NOTICE',
  CONFIDENTIALITY_POLICY = 'CONFIDENTIALITY_POLICY'
}

const defaultPublicDescription = createDictionary({
  [LinkTypes.DSA]: 'Content not adapted? You can <b>report the problem</b> by scanning this QR code',
  [LinkTypes.LEGAL_NOTICE]: 'Access to <b>legal notice</b>',
  [LinkTypes.CONFIDENTIALITY_POLICY]: 'Data Handling Policy',
}, '');

const HelpModal: React.FC<{ open?: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const ref = React.useRef<HTMLButtonElement>(null);
  const [linkType, setLinkType] = React.useState<LinkTypes>(LinkTypes.DSA);

  const appTheme = useSelector(getAppTheme);
  const appOrigin = useSelector(getAppOrigin);
  const isPublic = useSelector(isPublicDevice);
  const legalNotice = useSelector(getLegalNotice);
  const confidentialityPolicyLink = useSelector(getConfidentialityPolicyLink);
  const paymentMethods = useSelector(getPaymentMethods);
  const availableThemes = useSelector(getAvailableThemes);
  const shouldDisplayPhoneNumbers = useSelector(shouldDisplayPhoneNumbersSelector);
  const [currentTheme, setCurrentTheme] = React.useState<string>(appTheme);
  const [currentKioskDisplay, setCurrentKioskDisplay] = React.useState<string>(appOrigin || 'HOME');
  const hasTerms = Boolean(legalNotice || confidentialityPolicyLink);

  const hasPaymentMethods = paymentMethods?.length > 0;

  const themeData = React.useMemo(
    () => availableThemes?.reduce<Record<string, string>>(
      (acc, theme) => ({
        ...acc,
        [theme]: ucFirst(theme),
      }),
      {},
    ) ?? {},
    [availableThemes]
  );

  const handleChangeLinkType = (newLinkType: LinkTypes) => () => {
    setLinkType(newLinkType);
  };

  const handleChangeTheme = React.useCallback(
    (newTheme: string) => {
      setCurrentTheme(newTheme);
      addHtmlAttribute('data-theme', newTheme);
    },
    [],
  );

  const handleChangeKioskDisplay = React.useCallback(
    (display: string) => {
      setCurrentKioskDisplay(display);
      addHtmlAttribute('data-origin', display);
      // Also flip the device visibility so kiosk-only controls (footer button, logout...) follow the preview
      dispatch(dealersActions.overrideContext({ isPublic: PUBLIC_KIOSK_DISPLAYS.includes(display) }));
    },
    [dispatch],
  );

  const qrLink = {
    [LinkTypes.DSA]: DSA_URL,
    [LinkTypes.CONFIDENTIALITY_POLICY]: confidentialityPolicyLink,
    [LinkTypes.LEGAL_NOTICE]: legalNotice,
  }[linkType];

  return (
    <Modal
      open={open}
      focusRef={ref}
      className="modal-large max-h-[95vh] min-h-[300px] kioskPartteam:min-h-[600px] kioskSharebox:min-h-[400px] flex flex-col"
      title={<FormattedMessage id="helpLine.title" defaultMessage="Need help?" tagName="h2" />}
      onClose={onClose}
    >
      <div className="scroll-smooth flex-1 min-h-0 overflow-y-auto p-1">
        {hasPaymentMethods && (
          <>
            <div className="flex flex-col items-center">
              <p className="text-center">
                <FormattedMessage
                  id="helpLine.paymentMethods"
                  defaultMessage="Here are the methods of payment accepted at the dealership"
                />
              </p>
              <ul className="flex justify-center items-center w-full flex-wrap gap-4 my-4">
                {paymentMethods.map(({ name }) => (
                  <li key={name} className="flex items-center justify-center px-2 bg-card-bg rounded-md text-low">
                    {name}
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full h-px my-4 kioskSharebox:my-6 kiosk:my-8 bg-border-default" />
          </>
        )}
        {shouldDisplayPhoneNumbers && (
          <>
            <div className="flex flex-col items-center">
              <p className="text-center">
                <FormattedMessage
                  id="helpLine.message"
                  defaultMessage="If you have any problems with the terminal, do not hesitate to contact the number below."
                />
              </p>
              <DealersPhone />
            </div>
            <div className="w-full h-px my-s bg-border-default" />
          </>
        )}
        <div className="flex flex-col items-center text-center">
          {!isPublic && (
            <>
              <div className="text-center">
                <FormattedMessage
                  id="helpLine.DSA.privateTitle"
                  defaultMessage="Content not adapted? You can report the problem <a><b>here</b></a>"
                  values={{ a: getHyperlink({ href: DSA_URL, target: '_blank' }), b }}
                  tagName="p"
                />
              </div>
              {hasTerms && <HelpTerms legalNotice={legalNotice} confidentialityPolicyLink={confidentialityPolicyLink} />}
            </>
          )}
          {isPublic && (
            <>
              {hasTerms && (
                <div className="bg-overlay p-3xs w-auto flex justify-between rounded-button-list mb-s max-w-full">
                  <Button
                    className="truncate"
                    testId={LinkTypes.DSA}
                    size={ButtonSize.SMALL}
                    onClick={handleChangeLinkType(LinkTypes.DSA)}
                    type={linkType === LinkTypes.DSA ? ButtonType.WHITE : ButtonType.LIGHT}
                  >
                    <FormattedMessage id="helpLine.DSA.publicTitle" defaultMessage="Report a problem" />
                  </Button>
                  {legalNotice && <Button
                    className="truncate"
                    size={ButtonSize.SMALL}
                    testId={LinkTypes.LEGAL_NOTICE}
                    onClick={handleChangeLinkType(LinkTypes.LEGAL_NOTICE)}
                    type={linkType === LinkTypes.LEGAL_NOTICE ? ButtonType.WHITE : ButtonType.LIGHT}
                  >
                    <FormattedMessage id="helpLine.LEGAL_NOTICE.publicTitle" defaultMessage="Legal notice" />
                  </Button>}
                  {confidentialityPolicyLink &&  <Button
                    className="truncate"
                    size={ButtonSize.SMALL}
                    testId={LinkTypes.CONFIDENTIALITY_POLICY}
                    onClick={handleChangeLinkType(LinkTypes.CONFIDENTIALITY_POLICY)}
                    type={linkType === LinkTypes.CONFIDENTIALITY_POLICY ? ButtonType.WHITE : ButtonType.LIGHT}
                  >
                    <FormattedMessage id="helpLine.CONFIDENTIAL_POLICY.publicTitle" defaultMessage="Data Handling Policy" />
                  </Button>}
                </div>
              )}
              <p className="text-center text-s">
                <FormattedMessage
                  id={`helpLine.${linkType}.publicDescription`}
                  defaultMessage={defaultPublicDescription[linkType]}
                  values={{ b }}
                />
              </p>
              <div className="!w-1/2 !h-1/2 max-w-40 mt-s flex justify-center items-center p-2xs bg-overlay rounded-card">
                <QRCodeViewer content={qrLink} className="!size-full" />
              </div>
              <p className="text-center mt-s">
                <span className="kiosk:text-xl font-medium">{qrLink}</span>
              </p>
            </>
          )}
        </div>
        {isDevelopment && (
          <>
            <div className="w-full h-px my-4 kioskSharebox:my-6 kiosk:my-8 bg-border-default" />
            <div className="mb-8">
              <SelectInput
                value={currentTheme}
                data={themeData}
                onChange={handleChangeTheme}
                label={intl.formatMessage({ id: 'helpLine.changeTheme', defaultMessage: 'Select theme' })}
                className="max-h-24 kiosk:max-h-52"
                data-testid="theme-select"
              />
            </div>
            <div className="mb-8">
              <SelectInput
                value={currentKioskDisplay}
                data={KIOSK_DISPLAYS}
                inputOptionBottom
                onChange={handleChangeKioskDisplay}
                label="Kiosk display (dev)"
                className={classNames('max-h-44 kiosk:max-h-96 max-h-[160px] ', {
                  'max-h-28 kiosk:max-h-52': !hasPaymentMethods && !shouldDisplayPhoneNumbers && !isPublic,
                })}
                data-testid="kiosk-display-select"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default HelpModal;
